import enCommon from '../locales/en/common.json' with { type: 'json' };
import enAuth from '../locales/en/auth.json' with { type: 'json' };
import enSettings from '../locales/en/settings.json' with { type: 'json' };
import enErrors from '../locales/en/errors.json' with { type: 'json' };
import enValidation from '../locales/en/validation.json' with { type: 'json' };
import enFamily from '../locales/en/family.json' with { type: 'json' };
import enAdmin from '../locales/en/admin.json' with { type: 'json' };
import enPwa from '../locales/en/pwa.json' with { type: 'json' };
import enLearning from '../locales/en/learning.json' with { type: 'json' };
import enPlay from '../locales/en/play.json' with { type: 'json' };
import enNotifications from '../locales/en/notifications.json' with { type: 'json' };
import { HERO_I18N_FRAGMENT } from '@kidshome/hero-content';

/**
 * Recursively merge an authored catalog with the CO-LOCATED hero-content fragment (ADR-0110 §3) — the per-hero
 * `strings.yaml` translations the compiler emits. Returns a NEW object (the imported JSON is never mutated);
 * the fragment's leaves win on a key collision. Hero/cosmetic strings thus live beside the content, not
 * hand-maintained here. The fragment is small + first-party, so this is a one-time, build-stable merge.
 */
function mergeFragment<T extends Record<string, unknown>>(
  base: T,
  overlay: Record<string, unknown>,
): T {
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(overlay)) {
    const cur = out[k];
    out[k] =
      v &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      cur &&
      typeof cur === 'object' &&
      !Array.isArray(cur)
        ? mergeFragment(cur as Record<string, unknown>, v as Record<string, unknown>)
        : v;
  }
  return out as T;
}

const enCommonMerged = mergeFragment(enCommon, HERO_I18N_FRAGMENT.en.common);
const enPlayMerged = mergeFragment(enPlay, HERO_I18N_FRAGMENT.en.play);

/** Launch locales (ADR-0025). uk uses 4 CLDR plural forms. */
export const SUPPORTED_LOCALES = ['en', 'uk'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const FALLBACK_LOCALE: SupportedLocale = 'en';

/** Namespaces shipped in the initial chunk; feature namespaces (tasks, rewards, …) lazy-load later. */
export const DEFAULT_NS = 'common' as const;
export const NAMESPACES = [
  'common',
  'auth',
  'settings',
  'errors',
  'validation',
  'family',
  'admin',
  'pwa',
  'learning',
  'play',
  'notifications',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

/**
 * Initial resources: ONLY the fallback locale (`en`) is bundled eagerly (ADR-0025: keep the initial
 * chunk small; other locales lazy-load). `uk` is a dynamic import via {@link loadLocaleBundle}, so
 * it becomes its own Vite chunk and never inflates the kid-path initial JS.
 */
export const initialResources = {
  en: {
    common: enCommonMerged,
    auth: enAuth,
    settings: enSettings,
    errors: enErrors,
    validation: enValidation,
    family: enFamily,
    admin: enAdmin,
    pwa: enPwa,
    learning: enLearning,
    play: enPlayMerged,
    notifications: enNotifications,
  },
} as const;

export type Resources = (typeof initialResources)['en'];

/** Lazily load a locale's bundle (its own chunk). `en` is already bundled, so it short-circuits. */
export async function loadLocaleBundle(
  locale: SupportedLocale,
): Promise<Record<Namespace, Record<string, unknown>>> {
  if (locale === 'en') {
    return initialResources.en as unknown as Record<Namespace, Record<string, unknown>>;
  }
  // NOTE: do NOT add a `with { type: 'json' }` import attribute to these DYNAMIC imports. Vite serves
  // a JSON-module as JS, so at runtime the browser rejects the import on the `type: 'json'` assertion
  // ("Failed to fetch dynamically imported module") — the uk chunk never loads and language switching
  // silently no-ops. (Static JSON imports are fine: Vite inlines them at build.) moduleResolution
  // 'bundler' makes the attribute optional for tsc, so omitting it keeps both the build and runtime happy.
  const [
    common,
    auth,
    settings,
    errors,
    validation,
    family,
    admin,
    pwa,
    learning,
    play,
    notifications,
  ] = await Promise.all([
    import('../locales/uk/common.json').then((m) => m.default),
    import('../locales/uk/auth.json').then((m) => m.default),
    import('../locales/uk/settings.json').then((m) => m.default),
    import('../locales/uk/errors.json').then((m) => m.default),
    import('../locales/uk/validation.json').then((m) => m.default),
    import('../locales/uk/family.json').then((m) => m.default),
    import('../locales/uk/admin.json').then((m) => m.default),
    import('../locales/uk/pwa.json').then((m) => m.default),
    import('../locales/uk/learning.json').then((m) => m.default),
    import('../locales/uk/play.json').then((m) => m.default),
    import('../locales/uk/notifications.json').then((m) => m.default),
  ]);
  return {
    common: mergeFragment(common as Record<string, unknown>, HERO_I18N_FRAGMENT.uk.common),
    auth,
    settings,
    errors,
    validation,
    family,
    admin,
    pwa,
    learning,
    play: mergeFragment(play as Record<string, unknown>, HERO_I18N_FRAGMENT.uk.play),
    notifications,
  } as Record<Namespace, Record<string, unknown>>;
}
